import {
  ApiData,
  YesNoAnswer,
  SumInsuredType,
  PolicyIssued,
} from 'scenes/common/types';
import {
  LandlordQuoteDetails,
  LandlordQuoteData,
  PropertyPurposeType,
  KickOutChoice,
} from 'scenes/landlord/types';
import { getApiData } from 'scenes/common/helpers/apiData';
import { toCoverType } from 'scenes/common/api/quote/marshaller/coverage';
import moment from 'moment';
import { toMainAddress } from 'scenes/common/api/quote/marshaller/address';
import {
  toHasFlybuys,
  toFlybuysNumber,
} from 'scenes/common/api/quote/marshaller';
import {
  WireAsset,
  WirePurpose,
  WirePolicyRole,
  WirePartyType,
} from 'scenes/common/api/quote/types';

import { toDDMMYYYY } from 'scenes/common/helpers/datetime';

import {
  toOwnedProperty,
  toUsedForBusiness,
  toBuildingInformation,
  toAdditionalSecurityInstalled,
  toNumberOfBedrooms,
  toPropertyBuiltYear,
  toRoofMaterial,
  toWallMaterial,
  toSecurityFeatures,
  toSumInsuredType,
  booleanToYesNoUnspecified,
  toEstimatedSumInsured,
} from 'scenes/common/api/quote/marshaller/retrieve';

import { certificateOfOccupancyAnswerRequired } from 'scenes/landlord/selectors';

import {
  WireLandlordPolicyResponse,
  WireLandlordCoverageResponse,
  WireCoverageName,
  WireLandlordParty,
} from '../types';
import { toPromoCode } from './campaign';

const toPolicyStartDate = (policy: WireLandlordPolicyResponse): string => {
  if (policy.dates) return policy.dates.effective;
  return moment().format('YYYY-MM-DD');
};

const toPropertyPurpose = (assets: WireAsset[]): PropertyPurposeType => {
  const asset = assets[0];

  switch (asset?.purpose) {
    case WirePurpose.HldHmIncm:
      return PropertyPurposeType.HolidaysHouse;

    case WirePurpose.OwnNRented:
      return PropertyPurposeType.Rental;

    case WirePurpose.OwnNRentST:
      return PropertyPurposeType.ShortTermRental;

    case WirePurpose.OwnNLive:
      return PropertyPurposeType.PPR;

    default:
      return PropertyPurposeType.Unspecified;
  }
};

const toSumInsured = (
  coverages: WireLandlordCoverageResponse[],
  sumInsuredType: SumInsuredType
) => {
  const sumInsured = {
    agreedBuildingSumInsured: '',
    contentsSumInsured: '',
    rentalReturn: '',
  };

  const coverage = coverages[0];

  coverage?.sumInsureds?.forEach((insured) => {
    if (
      insured.type === WireCoverageName.Building &&
      sumInsuredType === SumInsuredType.Agreed
    ) {
      sumInsured.agreedBuildingSumInsured = insured.amount.toString();
    } else if (insured.type === WireCoverageName.Contents) {
      sumInsured.contentsSumInsured = insured.amount.toString();
    } else if (insured.type === WireCoverageName.RentalReturn) {
      sumInsured.rentalReturn = insured.amount.toString();
    }
  });

  return sumInsured;
};

const toHasRentalAgreement = (assets: WireAsset[]): YesNoAnswer => {
  const asset = assets[0];
  if (asset?.rentalAgreement) {
    return YesNoAnswer.Yes;
  }
  return YesNoAnswer.No;
};

const retrievePrimaryPolicyHolder = (
  parties: WireLandlordParty[]
): WireLandlordParty | undefined =>
  parties.find((party) => party.role === WirePolicyRole.PrimaryPolicyHolder);

// TODO - remove dummy return value when policy held by person,
// business is updated.
const toDOB = (parties: WireLandlordParty[]): string => {
  const party = retrievePrimaryPolicyHolder(parties);

  return toDDMMYYYY(party?.dob);
};

const toYearsInsured = (parties: WireLandlordParty[]): number => {
  if (parties[0]?.numberOfYearsInsured === undefined) {
    return 0;
  }
  return parseInt(parties[0].numberOfYearsInsured, 10);
};

const toClaimsMade = (parties: WireLandlordParty[]): number => {
  if (parties[0]?.claims?.last3Years === undefined) {
    return 0;
  }
  return parseInt(parties[0].claims.last3Years, 10);
};

// If claimsMade is zero, set broken in claims to be Unspecified
const toBrokenInClaims = (
  policy: WireLandlordPolicyResponse,
  claimsMade: number
): YesNoAnswer =>
  claimsMade
    ? booleanToYesNoUnspecified(policy.history?.hasBreakInClaims)
    : YesNoAnswer.Unspecified;

// Retrieve API returns hasCOO as false even if unanswered during quote creation.
// Therefore, return unspecified if no COC answer was required during quote
// creation.

export const toCertificateOfOccupancy = (assets: WireAsset[]): YesNoAnswer => {
  if (certificateOfOccupancyAnswerRequired(toPropertyBuiltYear(assets))) {
    return booleanToYesNoUnspecified(assets[0]?.building?.hasCOO);
  }
  return YesNoAnswer.Unspecified;
};

export const toPolicyHolder = (parties: WireLandlordParty[]): PolicyIssued => {
  const party = parties[0];
  if (party?.type === WirePartyType.Company) {
    return PolicyIssued.Business;
  }

  return PolicyIssued.Person;
};

export const toQuoteDetails = (
  quoteDetails: ApiData<LandlordQuoteDetails>
): LandlordQuoteData => {
  const { payload } = getApiData(quoteDetails);
  const sumInsuredType = toSumInsuredType(payload.assets);
  const {
    agreedBuildingSumInsured,
    contentsSumInsured,
    rentalReturn,
  } = toSumInsured(payload.coverages, sumInsuredType);

  const claimsMade = toClaimsMade(payload.parties);
  const brokenInClaims = toBrokenInClaims(payload.policy, claimsMade);

  return {
    prefilledCampaignId: '',
    prefilledPromoCode: '',
    colesCampaignCode: '',
    coverType: toCoverType(payload.coverType),
    policyStartDate: toPolicyStartDate(payload.policy),
    mainAddress: toMainAddress(payload.addresses),
    ownedProperty: toOwnedProperty(payload.assets),
    purposeOfProperty: toPropertyPurpose(payload.assets),
    kickOutChoice: KickOutChoice.Unspecified,

    hasRentalAgreement: toHasRentalAgreement(payload.assets),
    weeklyRentPaid: rentalReturn,

    propertyUsedForBusiness: toUsedForBusiness(payload.assets),
    policyHolderDateOfBirth:
      toPolicyHolder(payload.parties) === PolicyIssued.Person
        ? toDOB(payload.parties)
        : '',
    businessOwnerDateOfBirth:
      toPolicyHolder(payload.parties) === PolicyIssued.Business
        ? toDOB(payload.parties)
        : '',
    recentInsurer: payload.policy.history?.insurer || '',
    yearsInsured: toYearsInsured(payload.parties),
    claimsMade,
    policyIssued: toPolicyHolder(payload.parties),

    ...toBuildingInformation(payload.assets),

    brokenInClaims,
    additionalSecurityInstalled: toAdditionalSecurityInstalled(
      payload.assets,
      brokenInClaims
    ),
    numberOfBedrooms: toNumberOfBedrooms(payload.assets),
    propertyBuiltYear: toPropertyBuiltYear(payload.assets),
    certificateOfOccupancy: toCertificateOfOccupancy(payload.assets),
    roofMaterial: toRoofMaterial(payload.assets),
    wallMaterial: toWallMaterial(payload.assets),
    securityFeatures: toSecurityFeatures(payload.assets),
    estimatedSumInsured: toEstimatedSumInsured(payload.assets),
    agreedSumInsuredRange: {
      lowerLimit: '',
      upperLimit: '',
    },

    sumInsuredType,
    agreedBuildingSumInsured,
    contentsSumInsured,

    hasFlybuys: toHasFlybuys(payload.flybuys),
    flybuysNumber: toFlybuysNumber(payload.flybuys),
    promoCode: toPromoCode(payload.campaigns),
  };
};
